import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, FolderOpen, Grid3X3, Plus, Trash2 } from 'lucide-react';
import {
  VENDOR_PORTAL_KEYS,
  vendorPortalOf,
  type EventBooking,
  type EventVendorMetadataInput,
  type VendorFloorPlanGrant,
  type VendorFolderAccess,
  type VendorFolderGrant,
} from '@peles/shared';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { FolderPickerModal } from '../files/FolderPickerModal';
import { useFloorPlans } from '../../hooks/useFloorPlans';
import { usePatchBooking } from '../../hooks/useBookings';
import { useDialog } from '../../context/DialogContext';
import { fileTransport } from '../../api/files';
import { extractApiErrorMessage } from '../../utils/apiError';

const FOLDER_ACCESS_OPTIONS: { value: VendorFolderAccess; label: string; hint: string }[] = [
  { value: 'read', label: 'View & download', hint: 'Open and download, nothing more' },
  {
    value: 'upload',
    label: 'Upload',
    hint: 'Also upload and add folders; change or delete only their own',
  },
];

/** A shared folder's path from the event's files, read from the library. */
function FolderGrantPath({ nodeId, eventId }: { nodeId: string; eventId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['file-node-path', nodeId],
    queryFn: () => fileTransport.tree(nodeId),
    staleTime: 60_000,
  });
  if (nodeId.startsWith('event:')) return <>All event files</>;
  if (isLoading) return <span className="text-gray-400">Loading…</span>;
  if (isError || !data?.node) return <span className="text-amber-700">A folder since deleted</span>;
  // The path below the event — the event itself is the context here.
  const crumbs = data.breadcrumbs;
  const below = crumbs.slice(crumbs.findIndex((c) => c.nodeId === `event:${eventId}`) + 1);
  return <>{below.map((crumb) => crumb.name).join(' / ') || data.node.name}</>;
}

export interface VendorPortalAccessPanelProps {
  booking: EventBooking;
  eventId: string;
}

/**
 * What one vendor sees of this event in the vendor portal
 * (`@peles/shared` `utils/vendorPortal.ts`). Nothing until something is
 * shared: their expenses, their timeline, folders of the event's files (from
 * that folder down, view or upload) and floor plans (read only, guest names a
 * separate choice). Every login of the vendor sees the same.
 *
 * Each change is saved at once as a patch of its own key, so a switch here
 * never writes back the notes or the messaging grants as the page loaded them.
 */
export function VendorPortalAccessPanel({ booking, eventId }: VendorPortalAccessPanelProps) {
  const patch = usePatchBooking();
  const { alert } = useDialog();
  const { data: plans = [], isLoading: plansLoading } = useFloorPlans({ eventId });
  const [pickingFolder, setPickingFolder] = useState(false);

  /*
   * The panel's own copy of the grants, each change applied at once.
   *
   * ⚠️ A folder or plan list is saved whole, so an edit built from the
   * booking as last fetched would undo one saved a moment earlier whose
   * refetch had not arrived yet. The server's copy is adopted only while none
   * of this panel's saves is on the wire — the refetch after the last one
   * carries them all — and on a failed save, which puts the stored grants back.
   */
  const stored = vendorPortalOf(booking.metadata);
  const storedKey = JSON.stringify(stored);
  const [grants, setGrants] = useState(stored);
  const inFlight = useRef(0);
  const latestStored = useRef(storedKey);
  latestStored.current = storedKey;
  useEffect(() => {
    if (inFlight.current === 0) setGrants(JSON.parse(storedKey));
  }, [storedKey]);

  const save = (changes: EventVendorMetadataInput) => {
    inFlight.current += 1;
    setGrants((current) => ({
      ...current,
      ...(changes.portalExpenses !== undefined ? { canSeeExpenses: changes.portalExpenses } : {}),
      ...(changes.portalTimeline !== undefined ? { canSeeTimeline: changes.portalTimeline } : {}),
      ...(changes.portalFolders !== undefined ? { folders: changes.portalFolders } : {}),
      ...(changes.portalFloorPlans !== undefined ? { floorPlans: changes.portalFloorPlans } : {}),
    }));
    patch.mutate(
      { linkId: booking.linkId, patch: changes },
      {
        onSuccess: () => {
          inFlight.current -= 1;
        },
        onError: (err) => {
          inFlight.current -= 1;
          if (inFlight.current === 0) setGrants(JSON.parse(latestStored.current));
          void alert({
            title: 'Not saved',
            message: extractApiErrorMessage(err, 'The vendor’s access could not be saved.'),
            variant: 'error',
          });
        },
      },
    );
  };
  const saveFolders = (folders: VendorFolderGrant[]) =>
    save({ [VENDOR_PORTAL_KEYS.folders]: folders });
  const saveFloorPlans = (floorPlans: VendorFloorPlanGrant[]) =>
    save({ [VENDOR_PORTAL_KEYS.floorPlans]: floorPlans });

  const planName = (id: string) => plans.find((p) => p.id === id)?.name;
  const unsharedPlans = plans.filter(
    (plan) => !plan.isTemplate && !grants.floorPlans.some((g) => g.floorPlanId === plan.id),
  );

  const checkboxClass = 'mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500';
  const sectionTitle = 'text-sm font-semibold text-gray-900';

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        What {booking.vendorName} sees of this event when they sign in. Nothing is shown until you
        share it here. Who of theirs can sign in is on the{' '}
        <Link
          to={`/vendors/${booking.vendorId}`}
          className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
        >
          vendor page <ExternalLink size={12} />
        </Link>
        .
      </p>

      <section className="space-y-3">
        <h3 className={sectionTitle}>Their work</h3>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={grants.canSeeExpenses}
            disabled={patch.isPending}
            onChange={(e) => save({ portalExpenses: e.target.checked })}
            className={checkboxClass}
          />
          <span className="text-sm text-gray-700">
            Their expenses
            <span className="block text-xs text-gray-500">
              Their own lines, what was paid, and uploading a quote or invoice to one.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={grants.canSeeTimeline}
            disabled={patch.isPending}
            onChange={(e) => save({ portalTimeline: e.target.checked })}
            className={checkboxClass}
          />
          <span className="text-sm text-gray-700">
            Their timeline
            <span className="block text-xs text-gray-500">
              The timeline entries assigned to them.
            </span>
          </span>
        </label>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className={sectionTitle}>Files</h3>
          <button
            type="button"
            onClick={() => setPickingFolder(true)}
            disabled={patch.isPending}
            className="min-h-11 sm:min-h-9 flex items-center gap-1.5 px-3 text-sm text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 disabled:opacity-50"
          >
            <Plus size={15} /> Share a folder
          </button>
        </div>
        {grants.folders.length === 0 ? (
          <p className="text-sm text-gray-500">No folders shared.</p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
            {grants.folders.map((grant) => (
              <li
                key={grant.nodeId}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2"
                data-testid={`portal-folder-${grant.nodeId}`}
              >
                <FolderOpen size={16} className="flex-shrink-0 text-primary-600" />
                <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
                  <FolderGrantPath nodeId={grant.nodeId} eventId={eventId} />
                  <span className="block text-xs text-gray-500">and everything inside it</span>
                </span>
                <div className="w-full sm:w-48">
                  <SearchableListSelect
                    items={FOLDER_ACCESS_OPTIONS}
                    value={grant.access}
                    onChange={(value) =>
                      saveFolders(
                        grants.folders.map((g) =>
                          g.nodeId === grant.nodeId
                            ? { ...g, access: value as VendorFolderAccess }
                            : g,
                        ),
                      )
                    }
                    getValue={(o) => o.value}
                    getLabel={(o) => o.label}
                    getSubtitle={(o) => o.hint}
                    clearable={false}
                    disabled={patch.isPending}
                    ariaLabel="Access to this folder"
                  />
                </div>
                <button
                  type="button"
                  onClick={() =>
                    saveFolders(grants.folders.filter((g) => g.nodeId !== grant.nodeId))
                  }
                  disabled={patch.isPending}
                  aria-label="Stop sharing this folder"
                  title="Stop sharing"
                  className="min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 flex items-center justify-center rounded text-gray-400 hover:text-red-600 disabled:opacity-40"
                >
                  <Trash2 size={15} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h3 className={sectionTitle}>Floor plans</h3>
        {grants.floorPlans.length === 0 && (
          <p className="text-sm text-gray-500">No floor plans shared.</p>
        )}
        {grants.floorPlans.length > 0 && (
          <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
            {grants.floorPlans.map((grant) => (
              <li
                key={grant.floorPlanId}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2"
                data-testid={`portal-plan-${grant.floorPlanId}`}
              >
                <Grid3X3 size={16} className="flex-shrink-0 text-primary-600" />
                <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
                  {planName(grant.floorPlanId) ??
                    (plansLoading ? 'Loading…' : 'A plan since deleted')}
                  <span className="block text-xs text-gray-500">Read only</span>
                </span>
                <label className="flex min-h-11 sm:min-h-0 items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={grant.guestNames}
                    disabled={patch.isPending}
                    onChange={(e) =>
                      saveFloorPlans(
                        grants.floorPlans.map((g) =>
                          g.floorPlanId === grant.floorPlanId
                            ? { ...g, guestNames: e.target.checked }
                            : g,
                        ),
                      )
                    }
                    className={checkboxClass}
                  />
                  Show guest names
                </label>
                <button
                  type="button"
                  onClick={() =>
                    saveFloorPlans(
                      grants.floorPlans.filter((g) => g.floorPlanId !== grant.floorPlanId),
                    )
                  }
                  disabled={patch.isPending}
                  aria-label="Stop sharing this floor plan"
                  title="Stop sharing"
                  className="min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 flex items-center justify-center rounded text-gray-400 hover:text-red-600 disabled:opacity-40"
                >
                  <Trash2 size={15} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="sm:w-80">
          <SearchableListSelect
            items={unsharedPlans}
            value={null}
            onChange={(value) =>
              saveFloorPlans([
                ...grants.floorPlans,
                { floorPlanId: value as string, guestNames: false },
              ])
            }
            getValue={(plan) => plan.id}
            getLabel={(plan) => plan.name}
            placeholder="Share a floor plan…"
            emptyMessage={plans.length === 0 ? 'This event has no floor plans' : 'All shared'}
            isLoading={plansLoading}
            disabled={patch.isPending}
            clearable={false}
            ariaLabel="Share a floor plan"
          />
        </div>
      </section>

      {pickingFolder && (
        <FolderPickerModal
          open
          onClose={() => setPickingFolder(false)}
          rootNodeId={`event:${eventId}`}
          title={`Share a folder with ${booking.vendorName}`}
          confirmLabel="Share this folder"
          onPick={({ nodeId }) => {
            if (grants.folders.some((g) => g.nodeId === nodeId)) return;
            saveFolders([...grants.folders, { nodeId, access: 'read' }]);
          }}
        />
      )}
    </div>
  );
}
